import * as Dialog from '@radix-ui/react-dialog';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { KeyRound, LogOut, Mail, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { BackupCodes } from '../../routes/login';
import { Button } from '../ui/button';
import { NotificationSettingsDialog } from './email-notifications';
import { useReauth } from './reauth';

const item = 'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted';

export function UserMenu() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [emails, setEmails] = useState(false);
  const { withReauth } = useReauth();
  if (!me) return null;
  const initial = (me.name ?? me.email).slice(0, 1).toUpperCase();
  return (
    <>
      <Menu.Root>
        <Menu.Trigger asChild>
          <Button variant="ghost" size="icon" aria-label={`Account: ${me.name ?? me.email}`}>
            <span className="grid size-7 place-items-center rounded-full bg-muted text-xs font-semibold">{initial}</span>
          </Button>
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content align="end" sideOffset={6} className="z-50 min-w-60 rounded-md border bg-surface-raised p-1 shadow-md">
            <div className="px-2 py-1.5 text-sm">
              <p className="font-medium">{me.name ?? me.email}</p>
              <p className="text-xs text-muted-foreground">
                {me.email} · {me.role}
              </p>
            </div>
            <Menu.Separator className="my-1 h-px bg-border" />
            <Menu.Item className={item} onSelect={() => setEmails(true)}>
              <Mail className="size-4" aria-hidden="true" /> Notification settings
            </Menu.Item>
            <Menu.Item
              className={item}
              onSelect={async () => {
                // New codes replace the old ones, so they need a fresh authenticator code (task 46).
                const r = await withReauth(() => api<{ backupCodes: string[] }>('/auth/backup-codes', { body: {} })).catch(() => null);
                if (r) setCodes(r.backupCodes);
              }}
            >
              <KeyRound className="size-4" aria-hidden="true" /> New backup codes
            </Menu.Item>
            <Menu.Item
              className={item}
              onSelect={async () => {
                await api('/auth/logout', { body: {} });
                qc.clear();
                await navigate({ to: '/login', search: { next: undefined } });
              }}
            >
              <LogOut className="size-4" aria-hidden="true" /> Sign out
            </Menu.Item>
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
      <NotificationSettingsDialog open={emails} onOpenChange={setEmails} />
      <Dialog.Root open={!!codes} onOpenChange={o => !o && setCodes(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed top-1/2 left-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 space-y-3 rounded-lg border bg-surface-raised p-5 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <Dialog.Title className="font-semibold">New backup codes</Dialog.Title>
              <Dialog.Close aria-label="Close" className="rounded p-0.5 text-muted-foreground hover:text-foreground">
                <X className="size-4" aria-hidden="true" />
              </Dialog.Close>
            </div>
            <p className="text-sm text-muted-foreground">Your old backup codes no longer work.</p>
            {codes && <BackupCodes codes={codes} onDone={() => setCodes(null)} doneLabel="Done" />}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
